import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Calendar, Users, ShoppingCart, DollarSign, Package, Scissors, X } from 'lucide-react';

/**
 * Global Quick Actions Menu
 * Floating action button accessible from any page in the POS
 * Provides instant access to core tasks without navigation
 */
export const GlobalQuickActions: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const quickActions = [
    {
      id: 'book-appointment',
      label: 'Book Appointment',
      icon: <Calendar className="h-5 w-5" />,
      color: 'from-blue-500 to-blue-600',
      action: () => {
        navigate('/operations');
        // Dispatch event to open the new appointment modal in CalendarView
        setTimeout(() => window.dispatchEvent(new Event('openNewAppointment')), 100);
        setIsOpen(false);
      }
    },
    {
      id: 'add-client',
      label: 'Add Client',
      icon: <Users className="h-5 w-5" />,
      color: 'from-purple-500 to-purple-600',
      action: () => {
        navigate('/people', { state: { openAddClient: true } });
        toast.success('👤 Opening new client form');
        setIsOpen(false);
      }
    },
    {
      id: 'quick-checkout',
      label: 'Quick Checkout',
      icon: <ShoppingCart className="h-5 w-5" />,
      color: 'from-green-500 to-green-600',
      action: () => {
        navigate('/pos');
        toast.success('💰 Opening checkout');
        setIsOpen(false);
      }
    },
    {
      id: 'inventory',
      label: 'Inventory',
      icon: <Package className="h-5 w-5" />,
      color: 'from-orange-500 to-orange-600',
      action: () => {
        navigate('/inventory');
        toast.success('📦 Opening inventory');
        setIsOpen(false);
      }
    },
    {
      id: 'services',
      label: 'Services',
      icon: <Scissors className="h-5 w-5" />,
      color: 'from-pink-500 to-pink-600',
      action: () => {
        navigate('/services');
        toast.success('✂️ Opening services');
        setIsOpen(false);
      }
    },
    {
      id: 'cash-register',
      label: 'Financial',
      icon: <DollarSign className="h-5 w-5" />,
      color: 'from-emerald-500 to-emerald-600',
      action: () => {
        navigate('/financial');
        toast.success('💵 Opening financial module');
        setIsOpen(false);
      }
    }
  ];

  return (
    <>
      {/* Floating Action Button - mobile only (desktop has the full left sidebar; the FAB
          just overlapped content as a dark blob there — the "black spot", Jose 2026-09-08). */}
      <div className="fixed bottom-24 right-4 z-50 md:hidden">
        {/* Action Menu */}
        {isOpen && (
          <div className="absolute bottom-20 right-0 mb-2">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-3 min-w-[280px]">
              <div className="flex items-center justify-between mb-3 pb-3 border-b">
                <h3 className="font-semibold text-gray-900">Quick Actions</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-2">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={action.action}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-gradient-to-r ${action.color} text-white hover:shadow-lg transform hover:scale-105 transition-all`}
                  >
                    {action.icon}
                    <span className="font-medium">{action.label}</span>
                  </button>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-gray-500 text-center">
                  Quick access from anywhere
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main FAB Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl hover:shadow-3xl transform transition-all duration-200 flex items-center justify-center ${
            isOpen ? 'rotate-45 scale-110' : 'hover:scale-110'
          }`}
          title="Quick Actions"
        >
          {isOpen ? (
            <X className="h-8 w-8" />
          ) : (
            <div className="text-3xl">⚡</div>
          )}
        </button>

        {/* (Removed a hardcoded fake "3" notification badge — it was wired to nothing. Jose 2026-09-08) */}
      </div>

      {/* Background Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default GlobalQuickActions;
